import { useState, useRef, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { SpeechAnalysisService, type SpeechAnalysisResponse } from '../services/speechAnalysisService';
import { Mic, MicOff, Upload, TestTube, Activity, CheckCircle, XCircle } from 'lucide-react';

interface TestResult {
  success: boolean;
  data?: any;
  error?: string;
  timestamp: Date;
}

export default function SpeechAnalysisTestPage() {
  const { user } = useAuthStore();
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [targetWord, setTargetWord] = useState('hello');
  const [language, setLanguage] = useState<'en' | 'vi'>('en');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<SpeechAnalysisResponse | null>(null);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isTestingBackend, setIsTestingBackend] = useState(false);
  const [backendStatus, setBackendStatus] = useState<any>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Start recording audio
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Failed to start recording. Please check microphone permissions.');
    }
  }, []);

  // Stop recording audio
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  // Analyze speech using base64 data
  const analyzeSpeech = useCallback(async () => {
    if (!audioBlob || !user) return;

    setIsAnalyzing(true);
    try {
      const base64Data = await SpeechAnalysisService.blobToBase64(audioBlob);
      
      const result = await SpeechAnalysisService.analyzeSpeech({
        user_id: user.id,
        audio_file_path: base64Data,
        original_text: targetWord,
        language
      });

      setAnalysisResult(result);
      addTestResult({
        success: result.success,
        data: result.data,
        error: result.error,
        timestamp: new Date()
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addTestResult({
        success: false,
        error: errorMessage,
        timestamp: new Date()
      });
      console.error('Speech analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  }, [audioBlob, user, targetWord, language]);

  // Analyze speech using file upload
  const analyzeSpeechUpload = useCallback(async () => {
    if (!audioBlob || !user) return;

    setIsAnalyzing(true);
    try {
      const audioFile = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });
      
      const result = await SpeechAnalysisService.analyzeSpeechUpload({
        audio: audioFile,
        user_id: user.id,
        original_text: targetWord,
        language
      });

      setAnalysisResult(result);
      addTestResult({
        success: result.success,
        data: result.data,
        error: result.error,
        timestamp: new Date()
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addTestResult({
        success: false,
        error: errorMessage,
        timestamp: new Date()
      });
      console.error('Speech analysis upload failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  }, [audioBlob, user, targetWord, language]);

  // Test backend endpoints
  const testBackendEndpoints = useCallback(async () => {
    setIsTestingBackend(true);
    const results: TestResult[] = [];

    try {
      // Test backend status
      const statusResult = await SpeechAnalysisService.getBackendStatus();
      setBackendStatus(statusResult);
      results.push({
        success: true,
        data: statusResult,
        timestamp: new Date()
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      results.push({
        success: false,
        error: errorMessage,
        timestamp: new Date()
      });
    }

    try {
      // Test speech analysis endpoint
      const analysisTestResult = await SpeechAnalysisService.testSpeechAnalysis();
      results.push({
        success: true,
        data: analysisTestResult,
        timestamp: new Date()
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      results.push({
        success: false,
        error: errorMessage,
        timestamp: new Date()
      });
    }

    setTestResults(prev => [...prev, ...results]);
    setIsTestingBackend(false);
  }, []);

  // Add test result to the list
  const addTestResult = (result: TestResult) => {
    setTestResults(prev => [result, ...prev.slice(0, 9)]); // Keep last 10 results
  };

  // Clear audio
  const clearAudio = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
  };

  // Clear test results
  const clearTestResults = () => {
    setTestResults([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Speech Analysis Test</h1>
          <p className="text-gray-600 mb-6">
            Test the speech analysis backend API with audio recording and analysis capabilities.
          </p>

          {/* Backend Status Section */}
          <div className="mb-8 p-6 bg-gray-50 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Backend Status
              </h2>
              <button
                onClick={testBackendEndpoints}
                disabled={isTestingBackend}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <TestTube className="w-4 h-4" />
                {isTestingBackend ? 'Testing...' : 'Test Backend'}
              </button>
            </div>
            
            {backendStatus && (
              <div className="bg-white p-4 rounded-lg border">
                <pre className="text-sm text-gray-700 overflow-auto">
                  {JSON.stringify(backendStatus, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Audio Recording Section */}
          <div className="mb-8 p-6 bg-gray-50 rounded-xl">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Audio Recording</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Recording Controls */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
                      isRecording 
                        ? 'bg-red-600 text-white hover:bg-red-700' 
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {isRecording ? (
                      <>
                        <MicOff className="w-5 h-5" />
                        Stop Recording
                      </>
                    ) : (
                      <>
                        <Mic className="w-5 h-5" />
                        Start Recording
                      </>
                    )}
                  </button>
                  
                  {audioBlob && (
                    <button
                      onClick={clearAudio}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    >
                      Clear
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Word
                    </label>
                    <input
                      type="text"
                      value={targetWord}
                      onChange={(e) => setTargetWord(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter word to pronounce"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Language
                    </label>
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value as 'en' | 'vi')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="en">English</option>
                      <option value="vi">Vietnamese</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Audio Preview */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-800">Audio Preview</h3>
                {audioUrl ? (
                  <div className="space-y-3">
                    <audio controls className="w-full">
                      <source src={audioUrl} type="audio/webm" />
                      Your browser does not support the audio element.
                    </audio>
                    <div className="text-sm text-gray-600">
                      Duration: {audioBlob ? Math.round(audioBlob.size / 1000) : 0} KB
                    </div>
                  </div>
                ) : (
                  <div className="h-24 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500">
                    No audio recorded yet
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Analysis Section */}
          {audioBlob && (
            <div className="mb-8 p-6 bg-gray-50 rounded-xl">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Speech Analysis</h2>
              
              <div className="flex gap-4 mb-6">
                <button
                  onClick={analyzeSpeech}
                  disabled={isAnalyzing}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  {isAnalyzing ? 'Analyzing...' : 'Analyze (Base64)'}
                </button>
                
                <button
                  onClick={analyzeSpeechUpload}
                  disabled={isAnalyzing}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  {isAnalyzing ? 'Analyzing...' : 'Analyze (Upload)'}
                </button>
              </div>

              {analysisResult && (
                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Analysis Result</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Basic Info</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Transcription:</span> {analysisResult.data.transcription}</div>
                        <div><span className="font-medium">Overall Score:</span> {analysisResult.data.overall_score}</div>
                        <div><span className="font-medium">Language:</span> {analysisResult.data.language}</div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Feedback</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">EN:</span> {analysisResult.data.feedback_en}</div>
                        <div><span className="font-medium">VI:</span> {analysisResult.data.feedback_vi}</div>
                      </div>
                    </div>
                  </div>
                  
                  {analysisResult.data.phoneme_scores && analysisResult.data.phoneme_scores.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-medium text-gray-700 mb-3">Phoneme Scores</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {analysisResult.data.phoneme_scores.map((phoneme, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded-lg">
                            <div className="font-medium text-gray-800">{phoneme.phoneme}</div>
                            <div className="text-2xl font-bold text-blue-600">{phoneme.score}</div>
                            <div className="text-xs text-gray-600">{phoneme.feedback_en}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Test Results Section */}
          <div className="p-6 bg-gray-50 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Test Results</h2>
              <button
                onClick={clearTestResults}
                className="px-3 py-1 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Clear Results
              </button>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {testResults.length === 0 ? (
                <div className="text-gray-500 text-center py-8">
                  No test results yet. Run some tests to see results here.
                </div>
              ) : (
                testResults.map((result, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${
                      result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {result.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-sm font-medium ${
                            result.success ? 'text-green-800' : 'text-red-800'
                          }`}>
                            {result.success ? 'Success' : 'Error'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {result.timestamp.toLocaleTimeString()}
                          </span>
                        </div>
                        
                        {result.error && (
                          <div className="text-sm text-red-700 mb-2">
                            <span className="font-medium">Error:</span> {result.error}
                          </div>
                        )}
                        
                        {result.data && (
                          <details className="text-sm">
                            <summary className="cursor-pointer text-gray-700 hover:text-gray-900">
                              View Data
                            </summary>
                            <pre className="mt-2 p-2 bg-white rounded border text-xs overflow-auto">
                              {JSON.stringify(result.data, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
